import { useState, useEffect } from "react";
import {
  Box,
  Container,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  VStack,
  Alert,
  AlertIcon,
  HStack,
  FormControl,
  Input,
  Button,
  SimpleGrid,
  InputGroup,
  InputLeftElement,
  Text,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
} from "@chakra-ui/react";
import PageLoader from "./components/PageLoader";
import api from "./api";
import {
  CopyIcon,
  SearchIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from "@chakra-ui/icons";
import { Pagination } from "./components/Pagination";
import toast from "react-hot-toast";

const formatDate = (dateString: string | null): string => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
};

interface FeeUser {
  userId: string;
  walletPublicAddress: string;
  totalFeesPaid: number; // ETH
  totalFeesUsd: number;
  txCount: number;
  firstPaidAt: string | null;
  lastPaidAt: string | null;
}

interface PaginationData {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  limit: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

interface Statistics {
  totalFeesSum: number;
  totalFeesUsd: number;
  averageFees: number;
  averageFeesUsd: number;
  payingUsers: number;
  totalTransactions: number;
  ethPriceUsd: number;
}

const EMPTY_STATS: Statistics = {
  totalFeesSum: 0,
  totalFeesUsd: 0,
  averageFees: 0,
  averageFeesUsd: 0,
  payingUsers: 0,
  totalTransactions: 0,
  ethPriceUsd: 0,
};

const formatEth = (value: number) => `${(value || 0).toFixed(6)} ETH`;
const formatUsd = (value: number) =>
  `$${(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

function Fees() {
  const [feeUsers, setFeeUsers] = useState<FeeUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  }>({ key: "totalFeesPaid", direction: "desc" });
  const [feeRange, setFeeRange] = useState({
    minFees: "",
    maxFees: "",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [pagination, setPagination] = useState<PaginationData>({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 20,
    hasNextPage: false,
    hasPreviousPage: false,
  });
  const [statistics, setStatistics] = useState<Statistics>(EMPTY_STATS);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchFees = async () => {
    setLoading(true);
    setError("");
    try {
      const params: any = {
        page: currentPage,
        limit: itemsPerPage,
        sortBy: sortConfig.key,
        sortOrder: sortConfig.direction,
      };

      if (debouncedSearchTerm) {
        params.search = debouncedSearchTerm;
      }

      if (feeRange.minFees) {
        params.minFees = parseFloat(feeRange.minFees);
      }

      if (feeRange.maxFees) {
        params.maxFees = parseFloat(feeRange.maxFees);
      }

      const res = await api.get("/api/admin/fees/users", {
        params,
      });

      if (res.data.success) {
        setFeeUsers(res.data.data || []);
        setPagination(
          res.data.pagination || {
            currentPage: 1,
            totalPages: 1,
            totalCount: 0,
            limit: itemsPerPage,
            hasNextPage: false,
            hasPreviousPage: false,
          }
        );
        setStatistics({ ...EMPTY_STATS, ...res.data.statistics });
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || "Error loading fees data");
      toast.error(err?.response?.data?.message || "Error loading fees data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1); // Reset to page 1 when search changes
  }, [debouncedSearchTerm, feeRange.minFees, feeRange.maxFees]);

  useEffect(() => {
    fetchFees();
  }, [currentPage, itemsPerPage, sortConfig, debouncedSearchTerm, feeRange.minFees, feeRange.maxFees]);

  const handleSort = (key: string) => {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
    setCurrentPage(1);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchFees();
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setFeeRange({ minFees: "", maxFees: "" });
    setCurrentPage(1);
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const renderSortIcon = (key: string) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "asc" ? (
      <ChevronUpIcon ml={1} />
    ) : (
      <ChevronDownIcon ml={1} />
    );
  };

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={8} align="stretch">
        <Heading as="h1" size="xl" textAlign="center" color="white">
          User Fees Management
        </Heading>

        {/* Statistics Cards */}
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          <Box
            bg="gray.800"
            p={6}
            borderRadius="lg"
            borderWidth="1px"
            borderColor="gray.700"
          >
            <Stat>
              <StatLabel color="gray.400">Total Fees Collected</StatLabel>
              <StatNumber color="brand.400" fontSize="3xl">
                {formatEth(statistics.totalFeesSum)}
              </StatNumber>
              <StatHelpText color="gray.500">
                ≈ {formatUsd(statistics.totalFeesUsd)} ·{" "}
                {statistics.totalTransactions.toLocaleString()} fee transfers
              </StatHelpText>
            </Stat>
          </Box>
          <Box
            bg="gray.800"
            p={6}
            borderRadius="lg"
            borderWidth="1px"
            borderColor="gray.700"
          >
            <Stat>
              <StatLabel color="gray.400">Average Fees Per Paying User</StatLabel>
              <StatNumber color="brand.400" fontSize="3xl">
                {formatEth(statistics.averageFees)}
              </StatNumber>
              <StatHelpText color="gray.500">
                ≈ {formatUsd(statistics.averageFeesUsd)} across{" "}
                {statistics.payingUsers.toLocaleString()} paying users
                {!!statistics.ethPriceUsd &&
                  ` · ETH ${formatUsd(statistics.ethPriceUsd)}`}
              </StatHelpText>
            </Stat>
          </Box>
        </SimpleGrid>

        {/* Filters */}
        <Box
          bg="gray.800"
          p={6}
          borderRadius="lg"
          borderWidth="1px"
          borderColor="gray.700"
        >
          <VStack spacing={4} align="stretch">
            <HStack spacing={4} flexWrap="wrap">
              <InputGroup maxW="350px">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.300" />
                </InputLeftElement>
                <Input
                  placeholder="Search by user ID or wallet"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  bg="gray.900"
                  borderColor="gray.600"
                  color="white"
                  _placeholder={{ color: "gray.500" }}
                />
              </InputGroup>

              <FormControl maxW="150px">
                <Input
                  type="number"
                  placeholder="Min Fees (ETH)"
                  value={feeRange.minFees}
                  onChange={(e) =>
                    setFeeRange({ ...feeRange, minFees: e.target.value })
                  }
                  bg="gray.900"
                  borderColor="gray.600"
                  color="white"
                  _placeholder={{ color: "gray.500" }}
                  step="0.000001"
                />
              </FormControl>

              <FormControl maxW="150px">
                <Input
                  type="number"
                  placeholder="Max Fees (ETH)"
                  value={feeRange.maxFees}
                  onChange={(e) =>
                    setFeeRange({ ...feeRange, maxFees: e.target.value })
                  }
                  bg="gray.900"
                  borderColor="gray.600"
                  color="white"
                  _placeholder={{ color: "gray.500" }}
                  step="0.000001"
                />
              </FormControl>

              <Button colorScheme="brand" onClick={handleSearch} size="md">
                Apply Filters
              </Button>

              <Button
                variant="outline"
                onClick={handleClearFilters}
                size="md"
                borderColor="gray.600"
                color="gray.300"
                _hover={{ bg: "gray.700" }}
              >
                Clear
              </Button>
            </HStack>
          </VStack>
        </Box>

        {error && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            {error}
          </Alert>
        )}

        {loading && <PageLoader message="Loading fees data..." />}

        {!loading && (
          <>
            <Box
              overflowX="auto"
              bg="gray.800"
              borderRadius="lg"
              borderWidth="1px"
              borderColor="gray.700"
            >
              <Table variant="simple" colorScheme="gray">
                <Thead bg="gray.900">
                  <Tr>
                    <Th color="gray.400" borderColor="gray.700" whiteSpace="nowrap">
                      #
                    </Th>
                    <Th color="gray.400" borderColor="gray.700" whiteSpace="nowrap">
                      User ID
                    </Th>
                    <Th
                      color="gray.400"
                      borderColor="gray.700"
                      whiteSpace="nowrap"
                      cursor="pointer"
                      onClick={() => handleSort("walletPublicAddress")}
                      _hover={{ color: "white" }}
                    >
                      <HStack spacing={1}>
                        <Text>Wallet Address</Text>
                        {renderSortIcon("walletPublicAddress")}
                      </HStack>
                    </Th>
                    <Th
                      color="gray.400"
                      borderColor="gray.700"
                      whiteSpace="nowrap"
                      cursor="pointer"
                      onClick={() => handleSort("totalFeesPaid")}
                      _hover={{ color: "white" }}
                    >
                      <HStack spacing={1}>
                        <Text>Total Fees Paid</Text>
                        {renderSortIcon("totalFeesPaid")}
                      </HStack>
                    </Th>
                    <Th
                      color="gray.400"
                      borderColor="gray.700"
                      whiteSpace="nowrap"
                      cursor="pointer"
                      onClick={() => handleSort("txCount")}
                      _hover={{ color: "white" }}
                    >
                      <HStack spacing={1}>
                        <Text>Transfers</Text>
                        {renderSortIcon("txCount")}
                      </HStack>
                    </Th>
                    <Th
                      color="gray.400"
                      borderColor="gray.700"
                      whiteSpace="nowrap"
                      cursor="pointer"
                      onClick={() => handleSort("firstPaidAt")}
                      _hover={{ color: "white" }}
                    >
                      <HStack spacing={1}>
                        <Text>First Fee</Text>
                        {renderSortIcon("firstPaidAt")}
                      </HStack>
                    </Th>
                    <Th
                      color="gray.400"
                      borderColor="gray.700"
                      whiteSpace="nowrap"
                      cursor="pointer"
                      onClick={() => handleSort("lastPaidAt")}
                      _hover={{ color: "white" }}
                    >
                      <HStack spacing={1}>
                        <Text>Last Fee</Text>
                        {renderSortIcon("lastPaidAt")}
                      </HStack>
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {feeUsers.length === 0 ? (
                    <Tr>
                      <Td
                        colSpan={7}
                        textAlign="center"
                        color="gray.500"
                        py={10}
                      >
                        No fees data found
                      </Td>
                    </Tr>
                  ) : (
                    feeUsers.map((user, index) => (
                      <Tr key={user.userId} _hover={{ bg: "gray.750" }}>
                        <Td color="gray.300" borderColor="gray.700">
                          {(currentPage - 1) * itemsPerPage + index + 1}
                        </Td>
                        <Td
                          color="gray.300"
                          borderColor="gray.700"
                          fontFamily="mono"
                          fontSize="sm"
                        >
                          <HStack>
                            <Text title={user.userId}>
                              {user.userId.slice(0, 6)}...{user.userId.slice(-4)}
                            </Text>
                            <CopyIcon
                              cursor="pointer"
                              onClick={() => handleCopy(user.userId)}
                              _hover={{ color: "brand.400" }}
                            />
                          </HStack>
                        </Td>
                        <Td
                          color="gray.300"
                          borderColor="gray.700"
                          fontFamily="mono"
                          fontSize="sm"
                          whiteSpace="nowrap"
                        >
                          <HStack>
                            <Text title={user.walletPublicAddress}>
                              {user.walletPublicAddress.slice(0, 6)}...
                              {user.walletPublicAddress.slice(-4)}
                            </Text>
                            <CopyIcon
                              cursor="pointer"
                              onClick={() =>
                                handleCopy(user.walletPublicAddress)
                              }
                              _hover={{ color: "brand.400" }}
                            />
                          </HStack>
                        </Td>
                        <Td
                          color="brand.300"
                          borderColor="gray.700"
                          fontWeight="bold"
                          whiteSpace="nowrap"
                        >
                          {formatEth(user.totalFeesPaid)}
                          <Text fontSize="xs" color="gray.500" fontWeight="normal">
                            ≈ {formatUsd(user.totalFeesUsd)}
                          </Text>
                        </Td>
                        <Td color="gray.300" borderColor="gray.700">
                          {user.txCount}
                        </Td>
                        <Td
                          color="gray.300"
                          borderColor="gray.700"
                          fontSize="sm"
                          whiteSpace="nowrap"
                        >
                          {formatDate(user.firstPaidAt)}
                        </Td>
                        <Td
                          color="gray.300"
                          borderColor="gray.700"
                          fontSize="sm"
                          whiteSpace="nowrap"
                        >
                          {formatDate(user.lastPaidAt)}
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            </Box>

            {/* Pagination */}
            {pagination.totalCount > 0 && (
              <Pagination
                currentPage={currentPage}
                totalPages={pagination.totalPages}
                itemsPerPage={itemsPerPage}
                totalItems={pagination.totalCount}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={(items) => {
                  setItemsPerPage(items);
                  setCurrentPage(1);
                }}
              />
            )}
          </>
        )}
      </VStack>
    </Container>
  );
}

export default Fees;
